import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";

import { ConfigApi } from "../api";
import Config from "../Config";
import { DateDifference } from "../utilities/DateUtils";
import { setClientSessionId } from "../utilities/sessionContext";
import { useCartStore } from "./CartStore";

interface AppState {
	sessionId: string | null;
	cacheVersion: string | null;
	isDebug: boolean;
	websiteBanner: string | null;
	peakConfigs: any;
	cutoffs: any;
	staleCheck: Date | false;
	configApplied: boolean;
	applyConfig: (configData: any) => void;
	init: () => void;
	getSessionId: () => string | null;
	clearSessionId: () => void;
	getWebsiteBanner: () => string | null;
	resetLocalStorage: () => void;
	ToggleDebugMode: () => void;
	cacheVersionValidation: () => void;
}

const useAppStore = create<AppState>()(
	persist(
		devtools((set, get) => ({
			sessionId: null,
			cacheVersion: null,
			isDebug: false,
			websiteBanner: null,
			peakConfigs: null,
			staleCheck: false,
			configApplied: false,
			cutoffs: null,
			applyConfig: (configData) => {
				if (!configData) return;

				const cacheVersion = Config?.cacheVersion;

				// Set session ID in the client context for API requests
				if (configData?.session_id && typeof window !== "undefined") {
					setClientSessionId(configData.session_id);
				}

				set((prev) => ({
					...prev,
					sessionId: configData?.session_id,
					websiteBanner: configData?.promo_banner,
					peakConfigs: configData?.peak_configs,
					cutoffs: configData?.cutoffs,
					cacheVersion: prev?.cacheVersion ? prev.cacheVersion : cacheVersion,
					configApplied: true,
				}));
			},
			init: async () => {
				if (get().configApplied) return;
				const configData = await ConfigApi.GetConfig();
				get().applyConfig(configData);

				// Server-safe: only run client-specific checks when window is available
				const hasWindow = typeof window !== "undefined";
				if (!hasWindow) {
					return;
				}

				if (1 === 1) {
					const staleCheck = get().staleCheck;
					const staleTiming = 30; //minutes
					let newCheck = true;

					if (staleCheck) {
						const diff = DateDifference(new Date(), staleCheck);

						if (
							diff.hours > 0 ||
							diff.minutes > staleTiming ||
							diff.days > 0 ||
							!diff.hours ||
							!diff.minutes ||
							!diff.days
						) {
							//we are stale
							if (useAppStore.getState().isDebug) {
								console.log("AppStore.js - Stale state is detected, let's update.");
								useCartStore.getState().CheckCartItems();
							}
						}
					}

					if (newCheck) {
						if (useAppStore.getState().isDebug) {
							console.log("AppStore.js - Setting stale check for current time.");
						}
						set((prev) => ({ ...prev, staleCheck: new Date() }));
					}
				}

				//check to see if cookie is set
				// if (!document.cookie.includes("PHPSESSID")) {
				// 	document.cookie = `PHPSESSID=${configData.session_id}`;
				// }

				//check to see if we have items in the cart and their status
				const cart = useCartStore.getState().cart;
				if (cart && cart.length > 0) {
					useCartStore.getState().CheckCartItems();
				}
			},
			getSessionId: () => get().sessionId,
			clearSessionId: () => set((prev) => ({ ...prev, sessionId: null })),
			getWebsiteBanner: () => get().websiteBanner,
			resetLocalStorage: () => {
				localStorage.clear();
			},
			ToggleDebugMode: () => {
				set((prev) => ({ ...prev, isDebug: !prev.isDebug }));
			},
			cacheVersionValidation: () => {
				if (Config.cacheVersion !== get().cacheVersion) {
					console.log("Version mismatch, clearing local storage.");
					get().resetLocalStorage();
					set((prev) => ({ ...prev, cacheVersion: Config.cacheVersion }));
					window.location.reload();
				}
			},
		})),
		{
			name: "app-storage",
			merge: (persistedState, currentState) => {
				const hasCurrentConfig = Boolean(currentState?.sessionId) || currentState?.configApplied;
				if (hasCurrentConfig) {
					return { ...persistedState, ...currentState };
				}
				return { ...currentState, ...persistedState };
			},
		},
	),
);

export { useAppStore };
